/**
 * Button
 * Two button primitives shared across all atlas-forms apps.
 *
 * StandardButton — utility/icon buttons with minimal styling.
 *   Used for: close modals, dismiss toasts, toggle sidebar, tab switching, nav items.
 *   No theme variants — accepts a style prop for local overrides.
 *
 * DesignButton — fully themed design button with variants.
 *   Variants: primary, secondary, danger, ghost.
 *   StudioButton re-exports DesignButton for backward compatibility.
 */

import React, { useState } from 'react';
import { useUIStore } from '@atlas-forms/state-react';

// ─── StandardButton ──────────────────────────────────────────────────────────

export interface StandardButtonProps {
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  type?: 'button' | 'submit' | 'reset';
  'aria-label'?: string;
  'aria-current'?: 'page' | 'step' | 'location' | 'date' | 'time' | boolean;
  'aria-pressed'?: boolean;
  'aria-selected'?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  className?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export function StandardButton({
  onClick,
  children,
  icon,
  type = 'button',
  'aria-label': ariaLabel,
  'aria-current': ariaCurrent,
  'aria-pressed': ariaPressed,
  'aria-selected': ariaSelected,
  disabled,
  style,
  className,
  onMouseEnter,
  onMouseLeave,
}: StandardButtonProps) {
  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    background: 'none',
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    padding: 0,
    fontFamily: 'inherit',
    lineHeight: 1,
    opacity: disabled ? 0.5 : 1,
    flexShrink: 0,
  };

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-current={ariaCurrent}
      aria-pressed={ariaPressed}
      aria-selected={ariaSelected}
      className={className}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ ...baseStyle, ...style }}
    >
      {icon && <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>}
      {children}
    </button>
  );
}

// ─── DesignButton ─────────────────────────────────────────────────────────────

export interface DesignButtonProps {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md';
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
  /** Optional leading icon */
  icon?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  'aria-label'?: string;
}

export function DesignButton({
  variant = 'secondary',
  size = 'md',
  disabled = false,
  loading = false,
  type = 'button',
  onClick,
  children,
  icon,
  style,
  className,
  'aria-label': ariaLabel,
}: DesignButtonProps) {
  const { theme } = useUIStore();
  const isDark = theme === 'dark';
  const [hovered, setHovered] = useState(false);
  const isDisabled = disabled || loading;

  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    border: 'none',
    borderRadius: 6,
    fontFamily: 'inherit',
    fontWeight: 600,
    lineHeight: 1,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.5 : 1,
    transition: 'background 0.15s, border-color 0.15s, color 0.15s, opacity 0.15s',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    ...(size === 'sm'
      ? { padding: '6px 14px', fontSize: 12 }
      : { padding: '8px 20px', fontSize: 13 }),
  };

  const variantStyle = (): React.CSSProperties => {
    switch (variant) {
      case 'primary':
        return isDark
          ? {
              background: hovered ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.12)',
              border: '1px solid rgba(34,197,94,0.4)',
              color: '#22c55e',
            }
          : {
              background: hovered ? '#0e7490' : '#16a34a',
              border: '1px solid transparent',
              color: '#ffffff',
            };

      case 'danger':
        return {
          background: hovered ? '#dc2626' : '#ef4444',
          border: '1px solid transparent',
          color: '#ffffff',
        };

      case 'ghost':
        return isDark
          ? {
              background: hovered ? 'rgba(51,65,85,0.3)' : 'transparent',
              border: '1px solid transparent',
              color: hovered ? '#e2e8f0' : '#94a3b8',
            }
          : {
              background: hovered ? 'rgba(0,0,0,0.05)' : 'transparent',
              border: '1px solid transparent',
              color: hovered ? '#0f172a' : '#64748b',
            };

      case 'secondary':
      default:
        return isDark
          ? {
              background: hovered ? 'rgba(51,65,85,0.3)' : 'transparent',
              border: `1px solid ${hovered ? '#64748b' : 'rgba(51,65,85,0.6)'}`,
              color: hovered ? '#e2e8f0' : '#94a3b8',
            }
          : {
              background: hovered ? '#f1f5f9' : 'transparent',
              border: `1px solid ${hovered ? '#94a3b8' : '#cbd5e1'}`,
              color: hovered ? '#0f172a' : '#475569',
            };
    }
  };

  return (
    <button
      type={type}
      disabled={isDisabled}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
      className={className}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ ...base, ...variantStyle(), ...style }}
    >
      {icon && <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{icon}</span>}
      {loading ? 'Loading…' : children}
    </button>
  );
}
